import './ObjectiveDetails.css';

import React from 'react';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';
import { Box, Divider } from '@mui/material';
import { generateClassName } from '../../../b2b-common/helpers';
import DatesField from '../dates/DatesField';
import FieldText from '../../../b2b-common/FieldText';
import DimensionalValuesTable from './DimensionalValuesTable';
import { createDimensionalObjectivesFromRegularObjectives, getDimObjectiveType } from './helper';
import { getCurrentObjective } from '../helper';
import { getObjectiveType } from './QuantitativeObjectiveEditForm';
import ToleranceField from './ToleranceField';
import ApplicabilityField from './ApplicabilityField';

const StartEndValueQuantitative = ({ areaIndicator }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const currentObjective = getCurrentObjective(areaIndicator.objectiveDTOs)
    const objectiveType = getObjectiveType(currentObjective);
    const indicator = areaIndicator.indicatorDTO;

    const startValue = currentObjective.startQuantitativeValue;
    const endValue = currentObjective.endQuantitativeValue;
    const unit = indicator.quantitativeMeasurementUnitDTO?.quantitativeMeasurementUnit || '';

    const renderValues = () => {
        switch (objectiveType) {
            case ('Regular' || 'Range'):
                return (
                    <>
                        <FieldText labelAlign='left' textAlign='left' label={t('Start Value')} text={`${startValue} ${unit}`} />
                        <Divider orientation="vertical" flexItem variant="middle" light sx={{ mx: 2, bgcolor: '#131212' }} />
                        <FieldText labelAlign='left' textAlign='left' label={t('End Value')} text={`${endValue} ${unit}`} />
                    </>
                );
            case ('Threshold_Above' || 'Threshold_Below'):
                return (
                    <>
                        <FieldText labelAlign='left' textAlign='left' label={t('Threshold Value')} text={`${startValue} ${unit}`} className={'threshold_field'} />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>{renderValues()}</>
    );
}

const StartEndValueQualitative = ({ areaIndicator }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const currentObjective = getCurrentObjective(areaIndicator.objectiveDTOs)

    const startValue = currentObjective.startObjectiveValue.qualitativeValue;
    const endValue = currentObjective.endObjectiveValue.qualitativeValue;
    return (
        <>
            <FieldText labelAlign='left' textAlign='left' label={t('Start Value')} text={`${startValue}`} className='single-line-text' style={{ width: '45%'}} />
            <Divider orientation="vertical" flexItem variant="middle" light sx={{ mx: 2, bgcolor: '#131212' }} />
            <FieldText labelAlign='left' textAlign='left' label={t('End Value')} text={`${endValue}`} className='single-line-text' style={{ width: '45%' }} />
        </>
    );
}

const StartEndValueDimensional = ({ areaIndicator }) => {

    const dimObjs = createDimensionalObjectivesFromRegularObjectives(areaIndicator.objectiveDTOs);
    const currentObjective = getCurrentObjective(dimObjs);
    const indicator = areaIndicator.indicatorDTO;

    return (
        <DimensionalValuesTable
            dimensionalValues={currentObjective.valuesPerDimension}
            dimensionalType={indicator.dimensionalType}
            objectiveType={currentObjective.objectiveType}
            measurementUnit={indicator.quantitativeMeasurementUnitDTO?.quantitativeMeasurementUnit}
            onSelectedDimension={null}
            className={'simple-01'}
        />
    );
}

const AdvancedDetailsQuantitative = ({ areaIndicator }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const currentObjective = getCurrentObjective(areaIndicator.objectiveDTOs)
    return (
        <Box display="flex" width={'100%'} className="objective-details-advanced" gap={1.5} boxSizing={'border-box'}>
            <Box display="flex" p={2} width={'50%'}
                className="objective-details-tolerance obj-frame">
                <ToleranceField objective={currentObjective} />
            </Box>
            <Box display="flex" p={2} width={'50%'}
                className="objective-details-tolerance obj-frame">
                <ApplicabilityField objective={currentObjective} />
            </Box>
        </Box>
    );
}

const AdvancedDetailsDimensional = ({ areaIndicator }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const currentObjective = getCurrentObjective(areaIndicator.objectiveDTOs)
    return (
        null
    );
}

const StartEndValueProxy = ({ areaIndicator }) => {
    const indicator = areaIndicator.indicatorDTO;
    return (
        indicator.indicatorType === 'QUANTITATIVE' ?
            <StartEndValueQuantitative areaIndicator={areaIndicator}/> :
        indicator.indicatorType === 'QUALITATIVE' ?
            <StartEndValueQualitative areaIndicator={areaIndicator} /> :
        indicator.indicatorType === 'QUANTITATIVE_DIMENSIONAL' ?
            <StartEndValueDimensional areaIndicator={areaIndicator} /> :
            (null)
    );
}

const AdvancedDetailsProxy = ({ areaIndicator }) => {
    const indicator = areaIndicator.indicatorDTO;
    const currentObjective = getCurrentObjective(areaIndicator.objectiveDTOs)
    const objectiveType = getObjectiveType(currentObjective);
    return (
        objectiveType === 'Regular' ?
            (null) :
        indicator.indicatorType === 'QUANTITATIVE' ?
            <AdvancedDetailsQuantitative areaIndicator={areaIndicator} /> :
        indicator.indicatorType === 'QUANTITATIVE_DIMENSIONAL' ?
            <AdvancedDetailsDimensional areaIndicator={areaIndicator} /> :
            (null)            
    );
}

const ObjectiveDetails = ({ areaIndicator, className = null }) => {
    const currentObjective = getCurrentObjective(areaIndicator.objectiveDTOs)
    return (
        <Box className={generateClassName('objective-details', '', className)} display="flex" flexDirection="column" gap={1.5} width={'100%'} boxSizing={'border-box'} >
            <Box display="flex" flexDirection={'row'} gap={1.5} width={'100%'} boxSizing={'border-box'} className="objective-details-common">
                <Box display="flex" p={2} width={'50%'}
                    className="objective-details-dates obj-frame">
                    <DatesField startDate={currentObjective.startDate} endDate={currentObjective.endDate} />
                </Box>
                <Box display="flex" p={2} width={'50%'}
                    flexDirection={'row'} gap={3} justifyContent={'space-evenly'} 
                    className="objective-details-values obj-frame" >
                    <StartEndValueProxy areaIndicator={areaIndicator} />
                </Box>
            </Box>
            <AdvancedDetailsProxy areaIndicator={areaIndicator} />
        </Box>
    );
};

export default ObjectiveDetails;